/**
 * MashfaTopNav.tsx
 * ─────────────────────────────────────────────────────────────────
 * Horizontal top navigation bar with grouped dropdown submenus.
 * Brand · 4 group buttons (each opens a submenu mirroring the sidebar
 * groups) · search · bell · user pill.
 */

import { useState, useRef, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../auth/AuthProvider'
import { useUnreadCount } from '../../hooks/useMessaging'
import { useTodayAppointments } from '../../hooks/useDashboard'
import {
  Home, Calendar, Users, Stethoscope, Clock,
  FileText, Mail, FlaskConical, ScanLine, ClipboardList,
  CreditCard, ListChecks, ListTodo, CalendarClock, MessageSquare, MessageSquarePlus, FolderOpen, BarChart, Building, Activity, Eye, Download, Upload,
  Bell, Settings, Languages, BookOpen, LogOut, ScrollText,
  ChevronDown, Command,
  Hospital, HeartPulse, Briefcase, Cog,
  // Sprint R Phase R4+R5: Wallet for Expenses, PieChart for Owner-Dashboard
  Wallet, PieChart,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useFeatureFlag } from '../../hooks/useFeatureFlags'
import { useHasPermission } from '../../hooks/usePermissions'
import MashfaLogo from '../MashfaLogo'
import { LanguageSwitcher } from '../header/fh/LanguageSwitcher'
import ThemePicker from '../../pages/ThemePicker'
import './MashfaTopNav.css'

// ── Helpers ────────────────────────────────────────────────────────
type TFn = (key: string) => string

function getInitials(name: string, fallback: string): string {
  // Strip both arabic and english doctor-honorific prefixes before deriving initials
  const cleaned = name.replace(/^(د\.|Dr\.)\s*/, '').trim()
  if (!cleaned) return fallback
  const parts = cleaned.split(/\s+/)
  if (parts.length >= 2 && parts[0].length > 0 && parts[1].length > 0) {
    return parts[0][0] + parts[1][0]
  }
  return cleaned.slice(0, 2)
}

function roleLabel(role: unknown, t: TFn): string {
  if (!role) return t('roleTitles.default')
  const name = typeof role === 'object' && role !== null
    ? ((role as { label?: string; name?: string }).label || (role as { name?: string }).name || '')
    : String(role)
  switch (name) {
    case 'admin':     return t('roleTitles.admin')
    case 'doctor':    return t('roleTitles.doctor')
    case 'assistant': return t('roleTitles.assistant')
    case 'billing':   return t('roleTitles.billing')
    case 'nurse':     return t('roleTitles.nurse')
    default:          return name || t('roleTitles.default')
  }
}

const IconBell = () => (
  <svg fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
    <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 01-3.46 0" />
  </svg>
)

// ── Nav structure (mirrors sidebar groups) ───────────────────────
type NavItem = {
  to: string
  label: string
  Icon: React.ComponentType<{ size?: number | string; strokeWidth?: number | string }>
  badge?: number
  /** When set, the item renders as a button that runs this (e.g. open a modal) instead of navigating. */
  onClick?: () => void
}
type NavGroup = {
  key: string
  title: string
  Icon: React.ComponentType<{ size?: number | string; strokeWidth?: number | string }>
  items: NavItem[]
}

// ── Component ────────────────────────────────────────────────────
export function MashfaTopNav() {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const { data: unread } = useUnreadCount()
  const { data: todayAppts } = useTodayAppointments()
  const feedbackEnabled = useFeatureFlag('feedback_enabled')
  const canViewFeedback = useHasPermission('feedback.view')
  // Optional medical modules — shown only when licensed for the tenant.
  const labEnabled = useFeatureFlag('lab_enabled')
  const imagingEnabled = useFeatureFlag('imaging_enabled')
  const triageEnabled = useFeatureFlag('triage_enabled')
  const schedulingEnabled = useFeatureFlag('scheduling_enabled')
  const todosEnabled = useFeatureFlag('todos_enabled')
  // Sprint R Phase R4+R5 — Owner-only entries gated on permission codename.
  const canViewExpenses = useHasPermission('expenses.view')
  const canViewOwnerDashboard = useHasPermission('owner_dashboard.view')
  // Audit log is admin-only (backend _AdminOnly + AuditLog.tsx guard) — gate the
  // nav entry the same way so non-admins don't see a link that 403s.
  const canViewAudit = useHasPermission('admin.settings_access')
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [openGroup, setOpenGroup] = useState<string | null>(null)
  const userMenuRef = useRef<HTMLDivElement>(null)
  const navRef = useRef<HTMLDivElement>(null)

  // Close menus on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node
      if (userMenuOpen && !userMenuRef.current?.contains(target)) {
        setUserMenuOpen(false)
      }
      if (openGroup && !navRef.current?.contains(target)) {
        setOpenGroup(null)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [userMenuOpen, openGroup])

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setUserMenuOpen(false)
        setOpenGroup(null)
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [])

  // Close group dropdown on route change
  useEffect(() => { setOpenGroup(null) }, [location.pathname])

  const todayCount = Array.isArray(todayAppts) ? todayAppts.length : 0
  const unreadCount = unread?.unread_count ?? 0
  const hasNotifications = unreadCount > 0

  const groups: NavGroup[] = [
    {
      key: 'clinic', title: t('nav.klinik'), Icon: Hospital,
      items: [
        { to: '/calendar',     label: t('nav.kalender'),     Icon: Calendar, badge: todayCount > 0 ? todayCount : undefined },
        { to: '/patients',     label: t('nav.patienten'),    Icon: Users },
        { to: '/doctors',      label: t('nav.aerzte'),       Icon: Stethoscope },
        { to: '/waiting-room', label: t('nav.wartezimmer'),  Icon: Clock },
      ],
    },
    {
      key: 'medicine', title: t('nav.medizin'), Icon: HeartPulse,
      items: [
        { to: '/prescriptions',   label: t('nav.rezepte'),    Icon: FileText },
        { to: '/medical-reports', label: t('nav.arztbriefe'), Icon: Mail },
        // Optional modules — visible when the tenant has them licensed.
        ...(labEnabled ? [{ to: '/lab', label: t('nav.lab'), Icon: FlaskConical }] : []),
        ...(imagingEnabled ? [{ to: '/imaging', label: t('nav.imaging'), Icon: ScanLine }] : []),
        ...(triageEnabled ? [{ to: '/triage', label: t('nav.triage'), Icon: ClipboardList }] : []),
      ],
    },
    {
      key: 'admin', title: t('nav.verwaltung'), Icon: Briefcase,
      items: [
        // Sprint R Phase R5 — Owner-Dashboard prominent platziert.
        ...(canViewOwnerDashboard ? [{
          to: '/owner-dashboard', label: t('nav.ownerDashboard'), Icon: PieChart,
        }] : []),
        { to: '/billing',         label: t('nav.abrechnung'),    Icon: CreditCard },
        // Sprint R Phase R4 — Klinik-Ausgaben (Owner-only).
        ...(canViewExpenses ? [{
          to: '/expenses', label: t('nav.expenses'), Icon: Wallet,
        }] : []),
        { to: '/services',        label: t('nav.leistungen'),    Icon: ListChecks },
        { to: '/messaging',       label: t('nav.nachrichten'),   Icon: MessageSquare, badge: unreadCount > 0 ? unreadCount : undefined },
        { to: '/documents',       label: t('nav.dokumente'),     Icon: FolderOpen },
        ...(todosEnabled ? [{ to: '/tasks', label: t('nav.aufgaben'), Icon: ListTodo }] : []),
        { to: '/analytics',       label: t('nav.analytics'),     Icon: BarChart },
        // Resources = rooms/devices: a general clinic-management feature. Was
        // dental-only (PR-L7), which wrongly hid it from admins entirely
        // (admins have no specialization). Shown to all admin-area users now.
        { to: '/resources',       label: t('nav.resources'),     Icon: Building },
        { to: '/doctor-hours',    label: t('nav.arbeitszeiten'), Icon: Activity },
        { to: '/doctor-absences', label: t('nav.abwesenheiten'), Icon: Eye },
        ...(schedulingEnabled ? [{ to: '/shift-planning', label: t('nav.schichtplan'), Icon: CalendarClock }] : []),
        // Import/Export are admin data-management tools — shown in all modes
        // (incl. demo, where the catalog import is run). Previously demo-hidden.
        { to: '/import',        label: t('nav.import'),        Icon: Download },
        { to: '/export',        label: t('nav.export'),        Icon: Upload },
      ],
    },
    {
      key: 'system', title: t('nav.system'), Icon: Cog,
      items: [
        { to: '/notifications', label: t('nav.benachrichtigungen'), Icon: Bell },
        { to: '/settings',      label: t('nav.einstellungen'),     Icon: Settings },
        ...(canViewAudit ? [
          { to: '/audit-log',   label: t('nav.auditLog'),          Icon: ScrollText },
        ] : []),
        ...(__MASHFA_DEMO_MODE__ ? [] : [
          { to: '/translator',  label: t('nav.translator'),        Icon: Languages },
        ]),
        { to: '/guide',         label: t('nav.guide'),             Icon: BookOpen },
        ...(feedbackEnabled && canViewFeedback ? [
          { to: '/feedback',    label: t('feedback.nav_label', 'الملاحظات'), Icon: MessageSquarePlus },
        ] : []),
        ...(feedbackEnabled ? [
          { to: '/feedback', label: t('feedback.sendFeedback', 'Send Feedback'), Icon: MessageSquarePlus, onClick: () => window.dispatchEvent(new Event('open-feedback')) },
        ] : []),
      ],
    },
  ]

  const isPathActive = (to: string) =>
    to === '/' ? location.pathname === '/' : location.pathname.startsWith(to)
  const isGroupActive = (g: NavGroup) => g.items.some(it => isPathActive(it.to))
  const groupBadgeTotal = (g: NavGroup) =>
    g.items.reduce((sum, it) => sum + (it.badge ?? 0), 0)

  // Display name + initials — locale-aware doctor prefix (د. / Dr.)
  const rawName = user?.username ?? ''
  const docPrefix = t('common.doctorPrefix')
  const hasPrefix = rawName.startsWith('د.') || rawName.startsWith('Dr.')
  let displayName: string
  if (!rawName) {
    displayName = t('common.demoUserFallback')
  } else if (hasPrefix) {
    displayName = rawName
  } else {
    displayName = `${docPrefix} ${rawName}`
  }
  const avatarText = getInitials(displayName, t('common.initialsFallback'))
  const userRole = roleLabel(user?.role, t)

  return (
    <nav className="mashfa-topnav" role="banner">
      {/* ── Brand ─────────────────────────────────────── */}
      <Link to="/" className="brand" aria-label={t('header.brandHome')}>
        <MashfaLogo className="brand-blob" />
        <div className="brand-text">
          <span className="brand-heil">{t('header.brandFirst')}</span><span className="brand-port">{t('header.brandSecond')}</span>
        </div>
      </Link>

      {/* ── Group dropdowns ───────────────────────────── */}
      <div className="nav-main" ref={navRef}>
        <Link
          to="/"
          className={`nav-link${location.pathname === '/' ? ' active' : ''}`}
        >
          <Home size={16} strokeWidth={1.8} />
          {t('nav.dashboard')}
        </Link>

        {groups.map(g => {
          const total = groupBadgeTotal(g)
          const active = isGroupActive(g)
          const open = openGroup === g.key
          return (
            <div key={g.key} className="nav-group">
              <button
                type="button"
                className={`nav-link nav-group-btn${active ? ' active' : ''}${open ? ' open' : ''}`}
                onClick={() => setOpenGroup(open ? null : g.key)}
                aria-haspopup="menu"
                aria-expanded={open}
              >
                <g.Icon size={16} strokeWidth={1.8} />
                {g.title}
                <ChevronDown size={14} className="nav-group-chev" />
                {total > 0 && (
                  <span className="nav-badge">{total > 99 ? '99+' : total}</span>
                )}
              </button>

              <div
                className={`nav-submenu${open ? ' open' : ''}`}
                role="menu"
                aria-hidden={!open}
              >
                {g.items.map(it => {
                  const inner = (
                    <>
                      <it.Icon size={17} strokeWidth={1.7} />
                      <span className="nav-submenu-label">{it.label}</span>
                      {it.badge !== undefined && (
                        <span className="nav-badge nav-submenu-badge">
                          {it.badge > 99 ? '99+' : it.badge}
                        </span>
                      )}
                    </>
                  )
                  return it.onClick ? (
                    <button
                      key={it.label}
                      type="button"
                      className="nav-submenu-item"
                      onClick={() => { it.onClick!(); setOpenGroup(null) }}
                      tabIndex={open ? 0 : -1}
                    >
                      {inner}
                    </button>
                  ) : (
                    <Link
                      key={it.to}
                      to={it.to}
                      className={`nav-submenu-item${isPathActive(it.to) ? ' active' : ''}`}
                      onClick={() => setOpenGroup(null)}
                      tabIndex={open ? 0 : -1}
                    >
                      {inner}
                    </Link>
                  )
                })}
                {g.key === 'system' && <ThemePicker onOpen={() => setOpenGroup(null)} />}
              </div>
            </div>
          )
        })}
      </div>

      {/* ── Right side: search, bell, user ────────────── */}
      <div className="nav-right">
        <LanguageSwitcher />

        <button
          type="button"
          className="nav-icon-btn"
          aria-label={t('topbar.quickSearch')}
          onClick={() => window.dispatchEvent(new Event('open-command-palette'))}
        >
          <Command size={18} strokeWidth={1.8} />
        </button>

        <button
          type="button"
          className="nav-icon-btn"
          aria-label={t('nav.nachrichten')}
          onClick={() => navigate('/messaging')}
        >
          <MessageSquare size={18} strokeWidth={1.8} />
          {unreadCount > 0 && <span className="notif-dot" />}
        </button>

        <button
          type="button"
          className="nav-icon-btn"
          aria-label={t('topbar.notifications')}
          onClick={() => navigate('/notifications')}
        >
          <IconBell />
          {hasNotifications && <span className="notif-dot" />}
        </button>

        <div className="nav-user-wrap" ref={userMenuRef}>
          <button
            type="button"
            className="nav-user"
            data-testid="user-menu-btn"
            onClick={() => setUserMenuOpen(v => !v)}
            aria-haspopup="true"
            aria-expanded={userMenuOpen}
          >
            <div className="nav-user-avatar">{avatarText}</div>
            <div className="nav-user-info">
              <div className="nav-user-name">{displayName}</div>
              <div className="nav-user-role">{userRole}</div>
            </div>
          </button>

          {userMenuOpen && (
            <div className="nav-user-menu" role="menu">
              <Link to="/settings" onClick={() => setUserMenuOpen(false)}>
                <Settings size={16} />
                {t('nav.einstellungen')}
              </Link>
              <Link to="/guide" onClick={() => setUserMenuOpen(false)}>
                <BookOpen size={16} />
                {t('nav.guide')}
              </Link>
              <button
                type="button"
                data-testid="logout-btn"
                onClick={() => {
                  setUserMenuOpen(false)
                  logout()
                  // Explicit nav to /login so the URL bar leaves any tenant-scoped
                  // path (e.g. /patients/90018/) before the next user logs in.
                  // Without this, browser autocomplete or back-navigation can land
                  // the new user on a path their tenant has no access to → 404s.
                  navigate('/login', { replace: true })
                }}
              >
                <LogOut size={16} />
                {t('topbar.logout')}
              </button>
            </div>
          )}
        </div>
      </div>
    </nav>
  )
}

export default MashfaTopNav
